import React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, FileText, Trash2, ChevronLeft } from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';
import { useDeleteReport, useDownloadReport, useReports } from '../../hooks/useReportGeneration';
import { downloadGeneratedReport } from '../../utils/report-download';

const PAGE_SIZE = 20;

function formatFileSize(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function RecentReportsPage(): React.ReactElement {
  const [page, setPage] = useState(0);
  const offset = page * PAGE_SIZE;
  const { data, isLoading } = useReports(PAGE_SIZE, offset);
  const download = useDownloadReport();
  const deleteReport = useDeleteReport();

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Report History"
        description="All generated reports"
        helpSlug="reporting"
      />
      <Link to="/reporting" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
        <ChevronLeft className="h-4 w-4" aria-hidden />
        Back to Reporting
      </Link>

      <section className="rounded-xl border border-gray-200 bg-white p-6">
        {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
        <ul className="space-y-3">
          {data?.items.map((report) => (
            <li
              key={report.id}
              className="flex items-center justify-between rounded-lg border border-gray-100 p-4"
            >
              <div className="flex items-center gap-4">
                <FileText className="h-5 w-5 text-blue-600" aria-hidden />
                <div>
                  <p className="font-medium text-gray-900">{report.title}</p>
                  <p className="text-sm text-gray-600">
                    {report.createdAtDisplay} • {report.format.toUpperCase()} •{' '}
                    {formatFileSize(report.fileSizeBytes)}
                  </p>
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  aria-label={`Download ${report.title}`}
                  onClick={async () => {
                    try {
                      const res = await download.mutateAsync(report.id);
                      if (res.data?.downloadUrl) {
                        await downloadGeneratedReport(
                          res.data.downloadUrl,
                          report.title,
                          report.format,
                          report.createdAtDisplay
                        );
                      } else if (res.error) {
                        window.alert(res.error);
                      }
                    } catch (e) {
                      window.alert(e instanceof Error ? e.message : 'Download failed');
                    }
                  }}
                  className="rounded p-2 text-gray-400 hover:text-gray-600"
                >
                  <Download className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label={`Delete ${report.title}`}
                  onClick={async () => {
                    if (window.confirm(`Delete report "${report.title}"?`)) {
                      await deleteReport.mutateAsync(report.id);
                    }
                  }}
                  className="rounded p-2 text-gray-400 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-lg border px-3 py-1 text-sm disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page + 1} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border px-3 py-1 text-sm disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
